import { useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { Badge, Button } from '../atoms';
import { PageHeader } from '../shell';
import { DataTable, Pagination, CreateEntityModal, ListToolbar } from '../organisms';
import { useServerList } from '../lib/useServerList';
import { formatDate } from '../lib/orgTimezone';

/**
 * ReturnsList — /v2/filed-returns
 *
 * Global BPP "Return Search" workqueue over the REAL generic-CRUD list
 * (GET /api/filed-returns?q&status&re_pp&year&sort&dir&page&limit — src/routes/crud.js,
 * registered via route-registry; SEARCH_COLUMNS = account_number, owner_name). The
 * debounced search box, the status / year / RE-PP filters, the sortable column
 * headers, and the pager all drive the server (org + SoD row-scope enforced
 * server-side). No client-side filtering/slicing — the canonical list pattern
 * shared with ParcelsList / ClientsList / PropertiesList (PRD 06.1).
 *
 * Row click → /v2/filed-returns/:id; "+ New return" opens the create modal.
 */

interface FiledReturn {
  id: string;
  account_number: string | null;
  owner_name: string | null;
  tax_year: number;
  re_pp: 'real_estate' | 'personal_property' | null;
  type: string | null;
  status: ReturnStatus;
  due_date: string | null;
  file_on: string | null;
  reported_amount: number | null;
  assessor_value: number | null;
}

type ReturnStatus =
  | 'pending' | 'in_progress' | 'prepared' | 'reviewed'
  | 'filed' | 'filed_amended' | 'complete' | 'cancelled';

const statusVariant: Record<ReturnStatus, 'success' | 'warning' | 'neutral' | 'info' | 'danger'> = {
  pending: 'neutral', in_progress: 'warning', prepared: 'info', reviewed: 'info',
  filed: 'success', filed_amended: 'success', complete: 'success', cancelled: 'danger',
};
const statusLabel: Record<ReturnStatus, string> = {
  pending: 'Pending', in_progress: 'In progress', prepared: 'Prepared', reviewed: 'Reviewed',
  filed: 'Filed', filed_amended: 'Filed (amended)', complete: 'Complete', cancelled: 'Cancelled',
};

// filed_returns.status is an org-configurable taxonomy (migration 112 dropped the
// CHECK), so a return may carry a custom status the built-in maps don't cover.
// Fall back to a humanized label + neutral pill instead of a label-less pill.
const humanizeStatus = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const labelForStatus = (s: string): string => (s ? statusLabel[s as ReturnStatus] ?? humanizeStatus(s) : '—');
const variantForStatus = (s: string) => statusVariant[s as ReturnStatus] ?? 'neutral';

const STATUS_OPTIONS = (Object.keys(statusLabel) as ReturnStatus[]).map(s => ({ value: s, label: statusLabel[s] }));

const RE_PP_OPTIONS = [
  { value: 'real_estate', label: 'Real estate' },
  { value: 'personal_property', label: 'Personal property' },
];

// A descending window of tax years for the year filter (server-side `year=` param).
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 9 }, (_, i) => CURRENT_YEAR + 1 - i)
  .map(y => ({ value: String(y), label: String(y) }));

function money(v: number | null): string {
  return v != null ? `$${v.toLocaleString()}` : '—';
}

export function ReturnsList() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [rePpFilter, setRePpFilter] = useState('');
  // §6.6 — search returns by deadline: "everything due on/before this date".
  const [dueBeforeFilter, setDueBeforeFilter] = useState('');

  const list = useServerList<FiledReturn>({
    resource: 'filed-returns',
    queryKey: ['filed-returns-list'],
    defaultPageSize: 25,
    defaultSort: { id: 'tax_year', desc: true },
    filters: { status: statusFilter, re_pp: rePpFilter, year: yearFilter, due_before: dueBeforeFilter },
  });

  const columns = useMemo<ColumnDef<FiledReturn>[]>(() => [
    { accessorKey: 'account_number', header: 'Account #', cell: ({ getValue }) => <span style={{ fontFamily: 'var(--font-mono)' }}>{(getValue() as string) || '—'}</span> },
    { accessorKey: 'owner_name', header: 'Owner', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'tax_year', header: 'Year' },
    { accessorKey: 'type', header: 'Type', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 're_pp', header: 'RE/PP', cell: ({ getValue }) => { const v = getValue(); return v === 'real_estate' ? 'RE' : v === 'personal_property' ? 'PP' : '—'; } },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => {
      const s = String(getValue() ?? '');
      return <Badge variant={variantForStatus(s)} dot>{labelForStatus(s)}</Badge>;
    } },
    { accessorKey: 'due_date', header: 'Deadline', cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }) },
    { accessorKey: 'file_on', header: 'Filed on', cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }) },
    { accessorKey: 'reported_amount', header: 'Reported', cell: ({ getValue }) => money(getValue() as number | null) },
  ], []);

  const { query, rows, total } = list;
  const hasFilters = list.search !== '' || statusFilter !== '' || yearFilter !== '' || rePpFilter !== '' || dueBeforeFilter !== '';

  return (
    <>
      <PageHeader
        title="Returns"
        subtitle={query.isLoading ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'return' : 'returns'}`}
        actions={<Button variant="primary" size="md" onClick={() => setCreateOpen(true)}>+ New return</Button>}
      />

      <ListToolbar
        searchValue={list.searchInput}
        onSearchChange={list.setSearchInput}
        searchPlaceholder="Search returns by account # or owner…"
        filters={[
          { key: 'status', value: statusFilter, onChange: setStatusFilter, placeholder: 'All statuses', options: STATUS_OPTIONS },
          { key: 'year', value: yearFilter, onChange: setYearFilter, placeholder: 'All years', options: YEAR_OPTIONS },
          { key: 're_pp', value: rePpFilter, onChange: setRePpFilter, placeholder: 'All types', options: RE_PP_OPTIONS },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', margin: '0 0 var(--sp-4)' }}>
        <label htmlFor="due-before" style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Due on/before</label>
        <input
          id="due-before"
          type="date"
          value={dueBeforeFilter}
          onChange={(e) => setDueBeforeFilter(e.target.value)}
          style={{
            fontFamily: 'inherit', fontSize: 'var(--fs-sm)', padding: '6px 10px',
            border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)',
            background: 'var(--color-card)', color: 'var(--color-ink)',
          }}
        />
        {dueBeforeFilter && (
          <Button variant="ghost" size="sm" onClick={() => setDueBeforeFilter('')}>Clear deadline</Button>
        )}
      </div>

      <CreateEntityModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        table="filed_returns"
        routePath="filed-returns"
        invalidateKeys={[['filed-returns-list']]}
      />

      {query.error && (
        <div style={{ padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)', border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)', borderRadius: 'var(--r-lg)', marginBottom: 'var(--sp-4)' }}>
          <strong>Failed to load returns:</strong> {(query.error as Error).message}
        </div>
      )}

      {!query.isLoading && total === 0 && !query.error && (
        <div style={{ padding: hasFilters ? 'var(--sp-7)' : 'var(--sp-8)', background: 'var(--color-card)', border: '1px dashed var(--color-border)', borderRadius: 'var(--r-lg)', color: 'var(--color-muted)', textAlign: 'center' }}>
          {hasFilters
            ? <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>No returns match your search or filters.</p>
            : (
              <>
                <h2 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-xl)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink-3)' }}>No returns yet</h2>
                <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>Click "+ New return" to create one.</p>
              </>
            )}
        </div>
      )}

      {(total > 0 || query.isFetching) && !query.error && (
        <>
          <DataTable
            data={rows}
            columns={columns}
            sorting={list.sorting}
            onSortingChange={list.setSorting}
            onRowClick={(r) => navigate(`/filed-returns/${r.id}`)}
            emptyMessage={query.isFetching ? 'Loading…' : 'No returns'}
          />
          <Pagination
            pageIndex={list.pageIndex}
            pageCount={list.pageCount}
            pageSize={list.pageSize}
            totalRows={total}
            onPageChange={list.setPageIndex}
            onPageSizeChange={list.setPageSize}
          />
        </>
      )}
    </>
  );
}
